import { useSensors, useSensor, TouchSensor, MouseSensor, KeyboardSensor, DndContext, closestCenter, defaultDropAnimationSideEffects, defaultDropAnimation } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { useRef, useState, useMemo, Fragment } from 'react';
import { ContinueArrowIcon } from '@ui/kit/icons';
import { sequenceTileInput } from '@utils/component-framework';
import { NoSSR } from '@utils/react';
import { useComponentRootRef } from '../../../../../context.js';
import { useOpgavekompleksetEntry } from '../../../../../state/hook.entry.js';
import { useUpdateTaskEntryMutation } from '../../../../../state/mutations/update-task-entry.js';
import { TaskAutoScroller } from '../../../../../utils/dnd-kit.auto-scroll.js';
import { makeRestrictToRef } from '../../../../../utils/dnd-kit.restrict-to-container.js';
import { OpgavekompleksetTaskDragOverlay } from '../../../../atom.drag-overlay/_drag-overlay.js';
import { SequenceInput } from '../input.content.js';
import { DraggableInput } from '../input.draggable.js';
import { orderInputs } from '../util.order-inputs.js';
import { updateInputsOnDrag } from '../util.update-inputs-on-drag-over.js';
import { wrapper, container, circuit, tokens, item, getPointOnCircle } from './container.input.style.js';
import { jsx, jsxs } from 'react/jsx-runtime';

function CircuitInputContainer(props) {
  const componentRef = useComponentRootRef();
  const ref = useRef(null);
  const [draggedInputId, setDraggedInputId] = useState();
  const update = useUpdateTaskEntryMutation();
  const initialInputEntries = useOpgavekompleksetEntry({
    select: entry => Object.entries(entry?.data.tasks[props.taskEntryKey]?.details ?? {}).map(([key, value]) => {
      const typedValue = sequenceTileInput.entry.asTypeSafe(value);
      if (typedValue) {
        return {
          nodeId: key,
          ...typedValue
        };
      }
    }).filter(it => !!it)
  });
  const [inputEntries, setInputEntries] = useState(initialInputEntries);
  const inputs = useMemo(() => orderInputs(props.task.content, inputEntries), [inputEntries, props.task.content]);
  const draggableInputs = inputs.filter(it => !it.options.fixed);
  const sensors = useSensors(useSensor(TouchSensor, {
    activationConstraint: {
      distance: 6
    }
  }), useSensor(MouseSensor, {
    activationConstraint: {
      distance: 6
    }
  }), useSensor(KeyboardSensor, {
    scrollBehavior: "instant"
  }));
  const handleDragStart = event => {
    setDraggedInputId(event.active.id.toString());
  };
  const handleDragOver = event => {
    updateInputsOnDrag({
      taskEntryKey: props.taskEntryKey,
      inputs,
      update,
      updateUI: setInputEntries
    }, event);
  };
  const handleDragEnd = () => {
    setDraggedInputId(undefined);
  };
  const draggedInput = draggableInputs.find(it => it.id === draggedInputId);
  const amountOfItems = inputs.length * 2;
  const restrictToComponent = useMemo(() => makeRestrictToRef(componentRef), [componentRef]);
  return /*#__PURE__*/jsx("div", {
    className: wrapper,
    ref: ref,
    children: /*#__PURE__*/jsx(NoSSR, {
      children: /*#__PURE__*/jsxs(DndContext, {
        sensors: sensors,
        collisionDetection: closestCenter,
        onDragStart: handleDragStart,
        onDragOver: handleDragOver,
        onDragEnd: handleDragEnd,
        onDragCancel: handleDragEnd,
        modifiers: [restrictToComponent],
        autoScroll: false,
        children: [/*#__PURE__*/jsx(SortableContext, {
          items: draggableInputs,
          strategy: rectSortingStrategy,
          children: /*#__PURE__*/jsxs("div", {
            className: container,
            "data-is-drag-active": !!draggedInputId,
            children: [/*#__PURE__*/jsx("div", {
              className: circuit,
              children: inputs.map((input, index) => /*#__PURE__*/jsxs(Fragment, {
                children: [/*#__PURE__*/jsx("div", {
                  className: item,
                  style: tokens.__override({
                    offset: index * 2 / amountOfItems,
                    pointX: getPointOnCircle(index * 2 / amountOfItems).x,
                    pointY: getPointOnCircle(index * 2 / amountOfItems).y
                  }),
                  children: /*#__PURE__*/jsx("div", {
                    children: input.options.fixed ? /*#__PURE__*/jsx(SequenceInput, {
                      node: input,
                      taskEntryKey: props.taskEntryKey
                    }) : /*#__PURE__*/jsx(DraggableInput, {
                      node: input,
                      taskEntryKey: props.taskEntryKey,
                      isDraggingItem: draggedInputId === input.id
                    })
                  })
                }), /*#__PURE__*/jsx("div", {
                  className: item,
                  style: tokens.__override({
                    offset: (index * 2 + 1) / amountOfItems,
                    pointX: getPointOnCircle((index * 2 + 1) / amountOfItems).x,
                    pointY: getPointOnCircle((index * 2 + 1) / amountOfItems).y
                  }),
                  children: /*#__PURE__*/jsx(ContinueArrowIcon, {
                    height: 20
                  })
                })]
              }, input.id))
            }), /*#__PURE__*/jsx(OpgavekompleksetTaskDragOverlay, {
              task: props.task,
              dropAnimation: {
                ...defaultDropAnimation,
                sideEffects: defaultDropAnimationSideEffects({
                  styles: {
                    active: {
                      opacity: "0.5"
                    }
                  }
                })
              },
              children: draggedInput && /*#__PURE__*/jsx(SequenceInput, {
                isDragOverlay: true,
                isDraggable: true,
                node: draggedInput,
                taskEntryKey: props.taskEntryKey
              }, draggedInput.id)
            })]
          })
        }), /*#__PURE__*/jsx(TaskAutoScroller, {
          taskRef: ref,
          horisontalScroller: ref
        })]
      })
    })
  });
}

export { CircuitInputContainer };
//# sourceMappingURL=container.input.js.map
